<template>
  <div class="art-detail-modal special-panel-styles bg-white dark:bg-slate-900 rounded-md flex flex-col min-h-0">
    <div class="art-detail-modal__header">
      <div class="art-detail-modal__titles min-w-0 flex-1">
        <h3 class="art-detail-modal__title">
          {{ modalTitle }}
        </h3>
        <p class="art-detail-modal__subtitle">
          <template v-if="!isLoading && fetchError">
            {{
              $t(
                'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.ERROR'
              )
            }}
          </template>
          <template v-else-if="!isLoading && totalCount">
            {{
              $t(
                'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.SUBTITLE',
                { count: totalCount }
              )
            }}
          </template>
          <template v-else-if="!isLoading">
            {{
              $t(
                'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.EMPTY'
              )
            }}
          </template>
        </p>
      </div>
      <woot-button
        size="small"
        variant="clear"
        color-scheme="secondary"
        icon="dismiss"
        :title="
          $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.CLOSE')
        "
        @click="close"
      />
    </div>

    <div
      v-if="isLoading"
      class="art-detail-modal__loading"
    >
      <spinner size="40" />
      <span>{{
        $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.LOADING')
      }}</span>
    </div>

    <div
      v-else-if="fetchError"
      class="art-detail-modal__empty"
    >
      <empty-state
        :title="
          $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.ERROR')
        "
      />
    </div>

    <div
      v-else-if="!records.length"
      class="art-detail-modal__empty"
    >
      <empty-state
        :title="
          $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.EMPTY')
        "
      />
    </div>

    <div
      v-else
      class="art-detail-modal__body"
    >
      <div class="art-detail-modal__table-wrap">
        <table class="art-detail-modal__table">
          <thead>
            <tr>
              <th>
                {{
                  $t(
                    'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.TABLE_HEADER.CONVERSATION'
                  )
                }}
              </th>
              <th>
                {{
                  $t(
                    'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.TABLE_HEADER.TYPE'
                  )
                }}
              </th>
              <th>
                {{
                  $t(
                    'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.TABLE_HEADER.RESPONSE_TIME'
                  )
                }}
              </th>
              <th>
                {{
                  $t(
                    'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.TABLE_HEADER.TIMESTAMP'
                  )
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in records"
              :key="rowKey(row, index)"
              class="art-detail-modal__row"
              @click="openConversation(row)"
            >
              <td>
                <div class="art-detail-modal__conversation">
                  <span class="art-detail-modal__conversation-id">
                    #{{ row.conversation_display_id || '—' }}
                  </span>
                  <span
                    v-if="row.contact_name"
                    class="art-detail-modal__contact"
                    :title="row.contact_name"
                  >
                    {{ row.contact_name }}
                  </span>
                </div>
              </td>
              <td>{{ eventTypeLabel(row.event_type) }}</td>
              <td>{{ formatResponseTime(row.response_time_seconds) }}</td>
              <td>{{ formatTimestamp(row.occurred_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="totalCount > perPage"
        class="art-detail-modal__pagination"
      >
        <ve-pagination
          :total="totalCount"
          :page-index="page"
          :page-size="perPage"
          :page-size-option="[perPage]"
          @on-page-number-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { VePagination } from 'vue-easytable';
import { formatTime } from '@chatwoot/utils';
import format from 'date-fns/format';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import ReportsAPI from 'dashboard/api/reports';

const PER_PAGE = 25;

export default {
  name: 'AgentResponseTimeDetailModal',
  components: {
    Spinner,
    EmptyState,
    VePagination,
  },
  props: {
    agent: {
      type: Object,
      default: () => ({}),
    },
    since: {
      type: [Number, String],
      default: null,
    },
    until: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      records: [],
      totalCount: 0,
      page: 1,
      perPage: PER_PAGE,
      fetchError: false,
      fetchRequestId: 0,
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),
    modalTitle() {
      return this.$t(
        'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.TITLE',
        { agent: this.agent?.name || '—' }
      );
    },
  },
  watch: {
    agent: {
      immediate: true,
      handler(agent) {
        if (agent?.id) {
          this.page = 1;
          this.fetchDetails();
        }
      },
    },
    since() {
      if (this.agent?.id) {
        this.page = 1;
        this.fetchDetails();
      }
    },
    until() {
      if (this.agent?.id) {
        this.page = 1;
        this.fetchDetails();
      }
    },
  },
  methods: {
    rowKey(row, index) {
      return [
        row.conversation_id,
        row.event_type,
        row.occurred_at,
        index,
      ].join('-');
    },
    eventTypeLabel(eventType) {
      const key = `OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RESPONSE_TIME_DETAIL.EVENT_TYPE.${eventType}`;
      const translated = this.$t(key);
      return translated === key ? eventType : translated;
    },
    formatResponseTime(value) {
      if (value === null || value === undefined) return '—';
      return formatTime(value);
    },
    formatTimestamp(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return format(date, 'dd MMM yyyy, hh:mm a');
    },
    async fetchDetails() {
      if (!this.agent?.id || this.since == null || this.until == null) {
        this.records = [];
        this.totalCount = 0;
        this.fetchError = false;
        return;
      }

      const requestId = this.fetchRequestId + 1;
      this.fetchRequestId = requestId;
      this.isLoading = true;
      this.fetchError = false;
      try {
        const { data } = await ReportsAPI.getAgentResponseTimeDetails({
          userId: this.agent.id,
          since: this.since,
          until: this.until,
          page: this.page,
          perPage: this.perPage,
        });
        // Ignore stale responses from older page/filter requests.
        if (requestId !== this.fetchRequestId) return;
        this.records = Array.isArray(data?.data) ? data.data : [];
        this.totalCount = Number(data?.meta?.total_count) || 0;
        this.page = Number(data?.meta?.current_page) || this.page;
        this.perPage = Number(data?.meta?.per_page) || this.perPage;
      } catch (error) {
        if (requestId !== this.fetchRequestId) return;
        // eslint-disable-next-line no-console
        console.error(error);
        this.fetchError = true;
        this.records = [];
        this.totalCount = 0;
      } finally {
        if (requestId === this.fetchRequestId) {
          this.isLoading = false;
        }
      }
    },
    onPageChange(pageIndex) {
      this.page = pageIndex;
      this.fetchDetails();
    },
    close() {
      this.$emit('close');
    },
    openConversation(row) {
      if (!row?.conversation_display_id || !this.currentAccountId) return;
      this.close();
      this.$router.push(
        `/app/accounts/${this.currentAccountId}/conversations/${row.conversation_display_id}`
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.special-panel-styles {
  position: fixed;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.5);
  height: 80%;
  width: min(720px, 90vw);
  top: 50%;
  z-index: 40;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
}

.art-detail-modal__header {
  @apply flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-slate-100 dark:border-slate-700 shrink-0;
}

.art-detail-modal__title {
  @apply text-base font-semibold text-slate-800 dark:text-slate-100 m-0;
}

.art-detail-modal__subtitle {
  @apply text-xs text-slate-500 dark:text-slate-400 m-0 mt-1;
}

.art-detail-modal__loading,
.art-detail-modal__empty {
  @apply flex-1 flex items-center justify-center gap-3 text-slate-600 dark:text-slate-300 p-8;
}

.art-detail-modal__body {
  @apply flex flex-col flex-1 min-h-0;
}

.art-detail-modal__table-wrap {
  @apply flex-1 overflow-auto px-5;
}

.art-detail-modal__table {
  @apply w-full text-sm border-collapse;

  th {
    @apply sticky top-0 bg-white dark:bg-slate-900 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold py-3 border-b border-slate-100 dark:border-slate-700;
  }

  td {
    @apply py-3 border-b border-slate-50 dark:border-slate-800 text-slate-700 dark:text-slate-200 align-top;
  }
}

.art-detail-modal__row {
  cursor: pointer;

  &:hover td {
    @apply bg-slate-50 dark:bg-slate-800/60;
  }
}

.art-detail-modal__conversation {
  @apply flex flex-col min-w-0;
}

.art-detail-modal__conversation-id {
  @apply font-medium text-woot-600 dark:text-woot-400;
}

.art-detail-modal__contact {
  @apply text-xs text-slate-500 dark:text-slate-400 overflow-hidden whitespace-nowrap text-ellipsis max-w-[220px];
}

.art-detail-modal__pagination {
  @apply shrink-0 px-5 py-3 border-t border-slate-100 dark:border-slate-700 text-right;

  &:deep(.ve-pagination) {
    @apply bg-transparent dark:bg-transparent;
  }

  &:deep(.ve-pagination-select) {
    @apply hidden;
  }
}
</style>
